import React from "react";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Check } from "lucide-react";
import { BUSINESS } from "@/data/business";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { TikTokIcon } from "@/components/site/TikTokIcon";

const ContactHeader = () => (
  <section className="relative overflow-hidden">
    <div className="hero-accent absolute inset-0" aria-hidden="true" />
    <div className="container-x relative py-14 text-center sm:py-20">
      <Reveal className="mx-auto max-w-2xl">
        <span className="eyebrow">Visit &amp; Contact</span>
        <h1 className="mt-4 text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          We’d love to see you
        </h1>
        <p className="mt-6 text-base leading-relaxed text-[color:var(--text-muted)] sm:text-lg">
          By appointment only. Reach out or book online. Our studio in Niles is ready to welcome you.
        </p>
        <div className="mt-8 flex justify-center">
          <BookButton testId="contact-book-appointment-button" />
        </div>
      </Reveal>
    </div>
  </section>
);

const ContactDetailsCard = () => (
  <div className="glass h-full p-7 sm:p-8">
    <h2 className="text-2xl font-light tracking-tight">Get in touch</h2>
    <ul className="mt-6 space-y-5">
      <li className="flex items-start gap-3.5">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[color:rgba(217,183,122,0.34)]"><MapPin size={18} strokeWidth={1.75} /></span>
        <div>
          <div className="text-sm font-semibold">Address</div>
          <a href={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapsQuery)}`} target="_blank" rel="noreferrer" data-testid="contact-address" className="text-sm text-[color:var(--text-muted)] link-underline">{BUSINESS.address}</a>
        </div>
      </li>
      <li className="flex items-start gap-3.5">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[color:rgba(217,183,122,0.34)]"><Phone size={18} strokeWidth={1.75} /></span>
        <div>
          <div className="text-sm font-semibold">Phone</div>
          <a href={BUSINESS.phoneHref} data-testid="contact-phone" className="text-sm text-[color:var(--text-muted)] link-underline">{BUSINESS.phone}</a>
        </div>
      </li>
      <li className="flex items-start gap-3.5">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[color:rgba(217,183,122,0.34)]"><Mail size={18} strokeWidth={1.75} /></span>
        <div>
          <div className="text-sm font-semibold">Email</div>
          <a href={BUSINESS.emailHref} data-testid="contact-email" className="text-sm text-[color:var(--text-muted)] link-underline break-all">{BUSINESS.email}</a>
        </div>
      </li>
    </ul>

    <div className="mt-7 flex items-center gap-3">
      <a href={BUSINESS.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:rgba(43,31,23,0.14)] bg-[#FFFCF6]/60 transition-colors duration-300 hover:bg-[#FFFCF6]"><Instagram size={17} strokeWidth={1.75} /></a>
      <a href={BUSINESS.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:rgba(43,31,23,0.14)] bg-[#FFFCF6]/60 transition-colors duration-300 hover:bg-[#FFFCF6]"><Facebook size={17} strokeWidth={1.75} /></a>
      <a href={BUSINESS.social.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:rgba(43,31,23,0.14)] bg-[#FFFCF6]/60 transition-colors duration-300 hover:bg-[#FFFCF6]"><TikTokIcon /></a>
    </div>
  </div>
);

const ContactMap = ({ mapSrc }) => (
  <div className="glass h-full overflow-hidden p-2 sm:p-3">
    <iframe
      title="Bahra Skincare location map"
      data-testid="contact-map"
      src={mapSrc}
      className="h-[320px] w-full rounded-[14px] border-0 sm:h-full sm:min-h-[420px]"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  </div>
);

const HoursCard = () => (
  <div className="glass h-full p-7 sm:p-8">
    <div className="flex items-center gap-2.5">
      <Clock size={18} strokeWidth={1.75} />
      <h2 className="text-2xl font-light tracking-tight">Hours</h2>
    </div>
    <table data-testid="hours-table" className="mt-5 w-full text-sm">
      <tbody>
        {BUSINESS.hours.map((h) => (
          <tr key={h.day} className="border-b border-[color:rgba(43,31,23,0.08)] last:border-0">
            <td className="py-3 font-medium text-[color:var(--text)]">{h.day}</td>
            <td className={`py-3 text-right ${h.closed ? "text-[color:var(--text-subtle)]" : "text-[color:var(--text-muted)]"}`}>{h.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AmenitiesCard = () => (
  <div className="glass h-full p-7 sm:p-8">
    <h2 className="text-2xl font-light tracking-tight">Good to know</h2>
    <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
      {BUSINESS.amenities.map((a) => (
        <li key={a} className="flex items-center gap-2.5 text-sm text-[color:var(--text-muted)]">
          <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:rgba(217,183,122,0.4)]"><Check size={13} strokeWidth={2.25} className="text-[color:var(--text)]" /></span>
          {a}
        </li>
      ))}
    </ul>
  </div>
);

const PoliciesSection = () => (
  <section className="section-pad pt-0">
    <div className="container-x">
      <Reveal className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">Please Note</p>
        <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-4xl">Our policies</h2>
      </Reveal>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
        {BUSINESS.policies.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.06}>
            <div className="glass h-full p-7">
              <h3 className="text-lg font-medium">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--text-muted)]">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Contact() {
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapsQuery)}&output=embed`;

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact & Hours | Bahra Skincare in Niles, IL"
        description="Visit Bahra Skincare at 8307 W Golf Rd, Suite 144, Niles, IL 60714. See hours, parking and amenities, call (773) 600-1231 or book your appointment online. By appointment only."
        path="/contact"
      />
      <ContactHeader />

      {/* Info + Map */}
      <section className="section-pad pt-4">
        <div className="container-x grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <Reveal>
            <ContactDetailsCard />
          </Reveal>
          <Reveal delay={0.08}>
            <ContactMap mapSrc={mapSrc} />
          </Reveal>
        </div>
      </section>

      {/* Hours + Amenities */}
      <section className="section-pad pt-0">
        <div className="container-x grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <Reveal>
            <HoursCard />
          </Reveal>
          <Reveal delay={0.08}>
            <AmenitiesCard />
          </Reveal>
        </div>
      </section>

      <PoliciesSection />
    </div>
  );
}
